import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/datetime";
import { toast } from "sonner";
import { Trash2, FileSignature, Plus, X, Globe } from "lucide-react";
import Pagination, { PAGE_SIZE } from "../../components/Pagination";
import { useConfirm } from "../../components/ConfirmDialog";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["new", "in_review", "quoted", "won", "lost"];
const STATUS_LABEL = {
  new: "Nueva",
  in_review: "En revisión",
  quoted: "Cotizada",
  won: "Ganada",
  lost: "Perdida",
};

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const nav = useNavigate();
  const confirm = useConfirm();
  const { companies } = useCompany();
  const { isAdmin } = useAuth();

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/quotes", { params }).then((r) => setQuotes(r.data));
  };

  useEffect(() => {
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const visible = quotes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    toast.success("Estado actualizado");
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar solicitud?",
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Cotización eliminada");
    setSelected(null);
    setSelectedIds(new Set());
    load();
  };

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const visibleIds = visible.map((q) => q.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = await confirm({
      title: `¿Eliminar ${ids.length} solicitud${ids.length > 1 ? "es" : ""}?`,
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    const { data } = await api.post("/admin/quotes/bulk-delete", { ids });
    toast.success(`${data.deleted} eliminada${data.deleted !== 1 ? "s" : ""}`);
    setSelectedIds(new Set());
    load();
  };

  const saveNew = async () => {
    if (!creating?.name?.trim() || !creating?.email?.trim()) {
      toast.error("Nombre y email son obligatorios");
      return;
    }
    if (!creating?.project_type?.trim()) {
      toast.error("El tipo de proyecto es obligatorio");
      return;
    }
    try {
      const payload = {
        name: creating.name.trim(),
        email: creating.email.trim(),
        phone: creating.phone?.trim() || null,
        company: creating.company?.trim() || null,
        project_type: creating.project_type.trim(),
        industry: creating.industry?.trim() || null,
        timeline: creating.timeline?.trim() || null,
        budget_range: creating.budget_range?.trim() || null,
        description: creating.description?.trim() || null,
        partnership_interest: !!creating.partnership_interest,
        status: "new",
        modules: [],
        integrations: [],
        company_id: creating.company_id || null,
      };
      await api.post("/admin/quotes", payload);
      toast.success("Cotización creada");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear");
    }
  };

  return (
    <div data-testid="admin-quotes-page" className="p-8 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">
            {isAdmin ? "Sitio público + manuales · DoSoft" : "Mis cotizaciones"}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-ash-100">Cotizaciones</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="quote-create"
            onClick={() =>
              setCreating({
                name: "",
                email: "",
                phone: "",
                company: "",
                project_type: "",
                industry: "",
                timeline: "",
                budget_range: "",
                description: "",
                partnership_interest: false,
                company_id: companies[0]?.id || "",
              })
            }
            className="inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand hover:text-ink-800 font-semibold px-5 py-3 uppercase tracking-wider text-xs"
          >
            <Plus size={14} strokeWidth={1.5} /> Nueva cotización
          </button>
          <button
            data-testid="quotes-new-proposal-btn"
            onClick={() => nav("/admin/proposals/new")}
            className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-3 uppercase tracking-wider text-xs"
          >
            <FileSignature size={14} strokeWidth={1.5} />
            Nueva propuesta
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        {selectedIds.size > 0 && (
          <div data-testid="bulk-bar" className="flex items-center gap-2 mr-2 px-3 py-1.5 border border-brand bg-brand/10 text-xs">
            <span className="text-brand font-mono">{selectedIds.size} seleccionado{selectedIds.size !== 1 ? "s" : ""}</span>
            <button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              className="inline-flex items-center gap-1.5 bg-[#9B1C1C] hover:bg-[#7e1717] text-white px-3 py-1.5 uppercase tracking-widest font-semibold"
            >
              <Trash2 size={12} /> Eliminar
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="text-ash-300 hover:text-ash-100 ml-1">Cancelar</button>
          </div>
        )}
        <button
          data-testid="quotes-filter-all"
          onClick={() => setFilter("")}
          className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === "" ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
        >
          Todas ({quotes.length})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`quotes-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === s ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="border border-ink-500 bg-white overflow-x-auto">
        <table data-testid="quotes-table" className="w-full text-sm">
          <thead>
            <tr className="bg-brand-slate text-left">
              <th className="px-3 py-3 w-10">
                <input type="checkbox" data-testid="quotes-select-all" checked={allVisibleSelected} onChange={toggleAllVisible} className="accent-brand cursor-pointer" />
              </th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Solicitante</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Tipo</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Industria</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Presupuesto</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Estado</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer ${selectedIds.has(q.id) ? "bg-brand/5" : ""}`} onClick={() => setSelected(q)}>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" data-testid={`quote-select-${q.id}`} checked={selectedIds.has(q.id)} onChange={() => toggleId(q.id)} className="accent-brand cursor-pointer" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 text-ink-800 font-medium">
                    {q.name}
                    {!q.owner_id && (
                      <span title="Cotización recibida del formulario público" className="inline-flex items-center gap-1 bg-brand/15 text-brand-dark border border-brand/30 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-mono">
                        <Globe size={9} /> Pública
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ash-500">{q.company || q.email}</div>
                </td>
                <td className="px-4 py-3 text-ink-800">{q.project_type}</td>
                <td className="px-4 py-3 text-ink-800">{q.industry || "—"}</td>
                <td className="px-4 py-3 text-ink-800 font-mono text-xs">{q.budget_range || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest text-brand font-mono">{STATUS_LABEL[q.status] || q.status}</span></td>
                <td className="px-4 py-3 text-ink-800 font-mono text-xs">{formatDate(q.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`quote-delete-${q.id}`} onClick={(e) => { e.stopPropagation(); remove(q.id); }} className="text-ash-500 hover:text-destructive">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {quotes.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-brand-slate">No hay cotizaciones.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={quotes.length} onChange={setPage} />

      {selected && (
        <div className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-ink-800 border border-ink-500 max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">Cotización</div>
                <h2 className="font-display font-bold text-2xl text-ash-100">{selected.project_type}</h2>
                <div className="text-ash-300 text-sm mt-1">{selected.name} · {selected.company || "—"}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-ash-300 hover:text-ash-100">✕</button>
            </div>
            <dl className="space-y-3 text-sm border-t border-ink-500 pt-6">
              <Row label="Email">{selected.email}</Row>
              <Row label="Teléfono">{selected.phone || "—"}</Row>
              <Row label="Industria">{selected.industry || "—"}</Row>
              <Row label="Plazo">{selected.timeline || "—"}</Row>
              <Row label="Presupuesto">{selected.budget_range || "—"}</Row>
              <Row label="Socio comercial">
                {selected.partnership_interest ? (
                  <span className="text-brand font-mono uppercase tracking-widest text-xs">Sí, interesado</span>
                ) : (
                  "No"
                )}
              </Row>
              <Row label="Descripción"><span className="text-ash-100 whitespace-pre-wrap">{selected.description || "—"}</span></Row>
              <Row label="Fecha">{formatDateTime(selected.created_at)}</Row>
            </dl>
            <div className="border-t border-ink-500 mt-6 pt-6 space-y-4">
              <button
                onClick={() => nav(`/admin/proposals/new?from=${selected.id}`)}
                data-testid="quote-generate-proposal"
                className="inline-flex items-center justify-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-3 uppercase tracking-wider text-xs"
              >
                <FileSignature size={14} strokeWidth={1.5} />
                Generar propuesta formal
              </button>
              <div className="flex items-center gap-2 flex-nowrap overflow-x-auto">
                <span className="text-xs uppercase tracking-widest text-ash-300 shrink-0 mr-1">Estado:</span>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`quote-status-${s}`}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`shrink-0 px-3 py-2 border text-[11px] uppercase tracking-widest transition-colors ${selected.status === s ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"}`}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="quote-create-modal" className="bg-ink-800 border border-ink-500 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-500">
              <h2 className="font-display font-bold text-lg text-ash-100">Nueva cotización</h2>
              <button onClick={() => setCreating(null)} className="text-ash-500 hover:text-ash-100"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <QField label="Nombre *">
                <input data-testid="quote-create-name" className={qInput} value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
              </QField>
              <QField label="Email *">
                <input data-testid="quote-create-email" type="email" className={qInput} value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
              </QField>
              <QField label="Teléfono">
                <input className={qInput} value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
              </QField>
              <QField label="Empresa">
                <input className={qInput} value={creating.company} onChange={(e) => setCreating({ ...creating, company: e.target.value })} />
              </QField>
              <QField label="Tipo de proyecto *">
                <input data-testid="quote-create-type" className={qInput} value={creating.project_type} onChange={(e) => setCreating({ ...creating, project_type: e.target.value })} placeholder="Software a medida, App móvil…" />
              </QField>
              <QField label="Industria">
                <input className={qInput} value={creating.industry} onChange={(e) => setCreating({ ...creating, industry: e.target.value })} />
              </QField>
              <QField label="Timeline">
                <input className={qInput} value={creating.timeline} onChange={(e) => setCreating({ ...creating, timeline: e.target.value })} placeholder="3-6 meses" />
              </QField>
              <QField label="Presupuesto">
                <input className={qInput} value={creating.budget_range} onChange={(e) => setCreating({ ...creating, budget_range: e.target.value })} placeholder="$5M - $10M" />
              </QField>
              <QField label="Asignar a empresa">
                <select data-testid="quote-create-company-id" className={qInput} value={creating.company_id || ""} onChange={(e) => setCreating({ ...creating, company_id: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </QField>
              <div className="md:col-span-2">
                <QField label="Descripción">
                  <textarea rows={4} className={qInput} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} />
                </QField>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-ink-500 flex justify-end gap-3">
              <button onClick={() => setCreating(null)} className="px-4 py-2 border border-ink-500 text-ash-300 hover:text-ash-100 text-sm">Cancelar</button>
              <button data-testid="quote-create-save" onClick={saveNew} className="px-5 py-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover uppercase tracking-widest text-xs">
                Crear cotización
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const qInput = "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500";

function QField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-ash-500 font-mono mb-1">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="text-ash-300 text-xs uppercase tracking-widest font-mono">{label}</dt>
      <dd className="col-span-2 text-ash-100">{children}</dd>
    </div>
  );
}
